import React from "react";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SyncIcon from "@mui/icons-material/Sync";

const AUTO_SAVE_ON_LABEL = "Auto-save on";
const AUTO_SAVE_OFF_LABEL = "Auto-save off";
const AUTO_SAVE_SAVING_LABEL = "Auto-saving";
const COPY_LABEL = "Copy";
const MS_PER_SECOND = 1000;
const DEFAULT_AUTO_SAVE_SECONDS = 15;
const TOOLBAR_BUTTON_CLASS_NAME =
  "relative group flex h-7 w-7 items-center justify-center rounded border transition-colors";
const ACTIVE_AUTO_SAVE_CLASS_NAME =
  "border-success bg-success-surface text-success-text";
const INACTIVE_AUTO_SAVE_CLASS_NAME =
  "border-border bg-surface text-text hover:bg-surface-muted";
const COPY_BUTTON_CLASS_NAME =
  "ql-copy relative group flex h-7 w-7 items-center justify-center rounded border border-transparent text-text hover:border-border hover:bg-surface-muted";
const TOOLTIP_CLASS_NAME =
  "netpad-toolbar-tooltip absolute hidden group-hover:block whitespace-nowrap bg-text text-surface text-xs rounded-sm px-2 py-1 -bottom-8 left-1/2 transform -translate-x-1/2";

const getAutoSaveLabel = ({ isAutoSaveEnabled, isAutoSaveSaving }) => {
  if (isAutoSaveSaving) return AUTO_SAVE_SAVING_LABEL;

  return isAutoSaveEnabled ? AUTO_SAVE_ON_LABEL : AUTO_SAVE_OFF_LABEL;
};

const getAutoSaveIntervalSeconds = (autoSaveCheckIntervalMs) =>
  Math.round((autoSaveCheckIntervalMs ?? 0) / MS_PER_SECOND) ||
  DEFAULT_AUTO_SAVE_SECONDS;

const getAutoSaveTitle = ({ autoSaveCheckIntervalMs, isAutoSaveEnabled }) => {
  if (!isAutoSaveEnabled) return AUTO_SAVE_OFF_LABEL;

  const intervalSeconds = getAutoSaveIntervalSeconds(autoSaveCheckIntervalMs);
  return `${AUTO_SAVE_ON_LABEL}. Checking every ${intervalSeconds}s.`;
};

const getAutoSaveButtonClassName = (isAutoSaveEnabled) =>
  [
    TOOLBAR_BUTTON_CLASS_NAME,
    isAutoSaveEnabled
      ? ACTIVE_AUTO_SAVE_CLASS_NAME
      : INACTIVE_AUTO_SAVE_CLASS_NAME,
  ].join(" ");

const copyEditorText = async () => {
  const editor = document.querySelector(".ql-editor");
  if (!editor) return;

  const text = editor.innerText;
  const processedText = text.replace(/\n\s*\n/g, "\n").trim();
  await navigator.clipboard.writeText(processedText);
};

const TextStyleControls = () => (
  <>
    <select className="ql-header" defaultValue="">
      <option value="1"></option>
      <option value="2"></option>
      <option value=""></option>
    </select>
    <select className="ql-font" defaultValue="">
      <option value="">Normal</option>
      <option value="sans-serif">Sans Serif</option>
      <option value="palisade">Palisade</option>
    </select>
    <button className="ql-bold"></button>
    <button className="ql-italic"></button>
    <button className="ql-underline"></button>
    <button className="ql-strike"></button>
    <button className="ql-blockquote"></button>
    <button className="ql-code-block"></button>
    <select className="ql-align"></select>
    <button className="ql-list" value="ordered"></button>
    <button className="ql-list" value="bullet"></button>
    <button className="ql-indent" value="-1"></button>
    <button className="ql-indent" value="+1"></button>
    <button className="ql-link"></button>
    <button className="ql-image"></button>
    <button className="ql-video"></button>
  </>
);

const AutoSaveIcon = ({ isAutoSaveSaving }) => (
  <SyncIcon
    className={isAutoSaveSaving ? "animate-spin" : ""}
    fontSize="small"
  />
);

const AutoSaveToggleButton = ({
  autoSaveCheckIntervalMs,
  isAutoSaveEnabled,
  isAutoSaveSaving,
  onToggleAutoSave,
}) => {
  const autoSaveLabel = getAutoSaveLabel({
    isAutoSaveEnabled,
    isAutoSaveSaving,
  });
  const autoSaveTitle = getAutoSaveTitle({
    autoSaveCheckIntervalMs,
    isAutoSaveEnabled,
  });

  return (
    <button
      aria-label={autoSaveLabel}
      aria-pressed={isAutoSaveEnabled}
      className={getAutoSaveButtonClassName(isAutoSaveEnabled)}
      onClick={onToggleAutoSave}
      type="button"
    >
      <AutoSaveIcon isAutoSaveSaving={isAutoSaveSaving} />
      <span className={TOOLTIP_CLASS_NAME}>{autoSaveTitle}</span>
    </button>
  );
};

const CopyButton = () => {
  const handleCopy = async () => {
    try {
      await copyEditorText();
    } catch (error) {
      console.error("Failed to copy text:", error);
    }
  };

  return (
    <button
      aria-label={COPY_LABEL}
      className={COPY_BUTTON_CLASS_NAME}
      onClick={handleCopy}
      type="button"
    >
      <ContentCopyIcon fontSize="small" />
      <span className={TOOLTIP_CLASS_NAME}>{COPY_LABEL}</span>
    </button>
  );
};

const ToolbarActions = ({
  autoSaveCheckIntervalMs,
  isAutoSaveEnabled,
  isAutoSaveSaving,
  onToggleAutoSave,
}) => (
  <div className="ml-auto flex items-center space-x-2 mt-2 md:mt-0 font-[500]">
    <AutoSaveToggleButton
      autoSaveCheckIntervalMs={autoSaveCheckIntervalMs}
      isAutoSaveEnabled={isAutoSaveEnabled}
      isAutoSaveSaving={isAutoSaveSaving}
      onToggleAutoSave={onToggleAutoSave}
    />
    <div className="hidden md:flex">
      <CopyButton />
    </div>
  </div>
);

const CustomToolbar = ({
  autoSaveCheckIntervalMs,
  isAutoSaveEnabled = false,
  isAutoSaveSaving = false,
  onToggleAutoSave = () => {},
}) => (
  <div id="toolbar" className="font-montserrat flex flex-wrap items-center">
    <TextStyleControls />
    <ToolbarActions
      autoSaveCheckIntervalMs={autoSaveCheckIntervalMs}
      isAutoSaveEnabled={isAutoSaveEnabled}
      isAutoSaveSaving={isAutoSaveSaving}
      onToggleAutoSave={onToggleAutoSave}
    />
  </div>
);

export default CustomToolbar;
